import logger from '@/utils/logger';
import { zip, isValidDate } from '@/utils/tools';
import { timeParse, timeFormat } from 'd3-time-format';

// supported date formats: https://github.com/d3/d3-time-format#locale_format
const dateSpecifier = '%d.%m.%Y %H:%M';
export const dateParse = timeParse(dateSpecifier);
const tFormat = timeFormat(dateSpecifier);
export const dateFormat = (d) => d && tFormat(d);

const formatFnValue = (fnName) => (v) => {
  const value = `"${v}"`;
  return fnName ? `${fnName}(${value})` : value;
};

export function buildSearchQuery(_, params) {
  const [term] = params;
  return term ? `"${term}"` : '';
}

export function buildBasicQuery(filterParam, values) {
  const { iql_column: column, iql_operator: operators } = filterParam;
  if (!column || !operators.length) {
    throw new Error('Invalid arguments', column, operators, values);
  }
  if (!Array.isArray(values) || !values.length) {
    throw new Error('Cannot build basic query! You must provide an array of parameters');
  }
  if (operators.length !== values.length) {
    throw new Error(`Invalid filterParam! There are ${operators.length} IQL operators and ${values.length} corresponding values. The number of operators and values should match!`);
  }

  const [operator] = operators;
  const [value] = values;

  return value ? `${column} ${operator} "${value}"` : '';
}

export function buildSelectMultiQuery(filter, params) {
  const { iql_column: column, iql_operator: operators, iql_multiselect_operator: joinOperator } = filter;
  if (!column || !operators.length) {
    throw new Error('Invalid arguments', column, operators, params);
  }
  if (!joinOperator) {
    throw new Error('Missing multi-select join operator! Please, specify a valid join operator such as `OR` or `AND`');
  }

  const [operator] = operators;
  const paramsIQL = params
    .map((param) => `${column} ${operator} "${param}"`)
    .join(` ${joinOperator} `);

  return paramsIQL ? `(${paramsIQL})` : '';
}

export function buildSelectInQuery(filter, params) {
  const { iql_column: column, iql_operator: operators, iql_function: fn } = filter;
  if (!column || !operators.length) {
    throw new Error('Invalid arguments', column, operators, params);
  }

  const [operator] = operators;
  const format = formatFnValue(fn);

  /* eslint-disable operator-linebreak */
  return params.length ?
    `${column} ${operator} (${params.map(format).join(',')})` :
    '';
}

export function buildSelectQuery(filter, params) {
  const { iql_multiselect_operator: joinOperator } = filter;
  /* eslint-disable operator-linebreak */
  return joinOperator ?
    buildSelectMultiQuery(filter, params) :
    buildSelectInQuery(filter, params);
}

export function buildDateQuery(filter, params) {
  const { iql_column: column, iql_operator: operators } = filter;
  if (!column || !operators.length) {
    throw new Error('Invalid arguments', column, operators);
  }
  if (!(Array.isArray(params) && params.length === 2)) {
    throw new Error('You must provide an array of 2 parameters: a start and an end date');
  }
  if (!params.every((p) => (p === null) || (isValidDate(dateParse(p))))) {
    throw new Error(`You must provide valid dates in the following format: ${dateSpecifier}`);
  }

  const formatDateFn = formatFnValue('date');

  return zip(params, ['>=', '<='])
    .filter(([serializedDate]) => Boolean(serializedDate))
    .map(([serializedDate, operator]) => `${column} ${operator} ${formatDateFn(serializedDate)}`)
    .join(' AND ');
}

const queryBuilders = {
  fts: buildSearchQuery,
  basic: buildBasicQuery,
  select: buildSelectQuery,
  date: buildDateQuery,
};

export function buildQuery(filterParams) {
  const hasValues = ({ value }) => Array.isArray(value) && value.length;
  const queryBuilder = (type) => {
    const builder = queryBuilders[type];
    if (!builder) {
      // TODO: throw an error and handle it gracefully in the rendering phase ?
      // currently throwing an error breaks rendering and
      // watchers are not being recovered
      // throw new Error(`Unknown filter parameter type: ${type}! Cannot build an IQL query`);
      logger.warn(`Unknown filter parameter type: ${type}! Cannot build an IQL query`);
      return () => '';
    }
    return builder;
  };

  return filterParams.filter(hasValues)
    .map((param) => queryBuilder(param.type)(param, param.value))
    .filter(Boolean)
    .join(' AND ');
}

export function joinQuery(queries) {
  return queries.filter(Boolean).join(' AND ');
}
